// Create-organization modal open/close helpers — registered via
// context.functions so any element can dispatch them with:
//   el.call('openCreateOrgModal')
//   el.call('closeCreateOrgModal')
//
// Migrated 2026-06-01 from `root.update({ _createOrgModalOpen: true })`
// to a @symbo.ls/windows custom-template layer. All `_createOrg*` state
// fields (name, slug, dirty, status, suggestion, conflict, error, busy)
// are RETAINED as data carriers — the body's reactive reads on them are
// unchanged. Only the visibility flag moves to the layer.

const CREATE_ORG_LAYER_ID = 'create-org'

// Persisted "I chose to stay on my personal workspace" flag — set by
// `usePersonalWorkspace` so the onboarding gate (NoOrgOverlay) never re-opens
// the modal for this user on subsequent loads. (2026-06-17 per Nika.)
const ONBOARDING_SKIP_KEY = 'symbols_org_onboarding_skipped'

export const isOrgOnboardingSkipped = () => {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(ONBOARDING_SKIP_KEY) === '1'
  } catch {
    return false
  }
}

const _rootState = (el) =>
  el?.state?.root || el?.getRoot?.()?.state?.root || el?.getRoot?.()?.state || null

const _resetFields = {
  _createOrgName: '',
  _createOrgSlug: '',
  _createOrgSlugDirty: false,
  _createOrgSlugChecking: false,
  _createOrgSlugStatus: null,
  _createOrgSlugSuggestion: null,
  _createOrgSlugConflict: null,
  _createOrgError: null,
  _createOrgBusy: false,
  // Onboarding mode (NoOrgOverlay gate): swaps Cancel for "Use personal
  // workspace" and the modal opens non-dismissable. Off for the normal
  // switcher / dashboard "+ Create workspace" path.
  _createOrgOnboarding: false
}

export function openCreateOrgModal() {
  const root = _rootState(this)
  if (!root) return
  // Reset the draft fields so reopening doesn't show a stale state.
  root.update(_resetFields)
  // MODAL-X-OVERFLOW-1: `openCustomModal` (shared/functions) supplies a
  // default `maxHeight` so a short viewport can't push ModalHeader (and its
  // Close X) off-screen with no scroll back to it — plain `openModal` here
  // used to leave that ceiling unset. Paired with AppCreateOrgModal's own
  // `maxHeight:'80vh'` root cap; the body's `Fields` region is what
  // actually scrolls.
  this.call('openCustomModal', {
    id: CREATE_ORG_LAYER_ID,
    // THE DIALOG'S NAME (persona audit WL-9): @symbo.ls/windows names the
    // window by `aria-labelledby="modal-title-<id>"`, and with no `title` the
    // first heading carrying that id is the HIDDEN Confirm template's — the
    // dialog read as "Heading" / "Are you sure?". The title names it.
    title: this.call('polyglot', 'workspace.createOrg.title'),
    content: { extends: 'AppCreateOrgModal' }
  })
}

export function closeCreateOrgModal() {
  const root = _rootState(this)
  // Clear all the draft fields back to defaults — matches what the old
  // `extraClose` map did when stateKey-driven `_createOrgModalOpen` flipped null.
  root?.update(_resetFields)
  this.call('closeModal', CREATE_ORG_LAYER_ID)
}

// Onboarding variant — opened by the NoOrgOverlay gate when a signed-in user
// has no team org. Same AppCreateOrgModal body, but onboarding mode (Cancel →
// "Use personal workspace") and a non-dismissable backdrop so the user must
// make a choice. (2026-06-17 per Nika "NoOrgOverlay should be moved as
// @symbo.ls/windows modal".)
export function openOnboardingWorkspaceModal() {
  const root = _rootState(this)
  if (!root) return
  root.update({ ..._resetFields, _createOrgOnboarding: true })
  // MODAL-X-OVERFLOW-1 (see openCreateOrgModal above) — doubly important
  // here: this variant is non-dismissable (no backdrop/ESC close), so a
  // header pushed off-screen would strand the user with no visible way out.
  this.call('openCustomModal', {
    id: CREATE_ORG_LAYER_ID,
    dismissOnBackdrop: false,
    // Named by its title, as above (WL-9).
    title: this.call('polyglot', 'workspace.createOrg.title'),
    content: { extends: 'AppCreateOrgModal' }
  })
}

// The dialog's first field takes the keyboard (persona audit WL-9: focus stayed
// on the page body when it opened). The onboarding gate opens the dialog while
// the boot screen still keeps every body child `inert` (index.html
// lockBodyInput, ~3 s), and a focus() under it is a silent no-op — so this
// answers false there, and the field asks again at `smbls:boot-ready`, when
// the lock lifts. It never takes the keyboard from another field the person
// is already typing in. `input` is the field's element (`el.call` passes it;
// `this` otherwise). → true when the field holds focus.
export function focusCreateOrgName(input) {
  const el = input || this
  const node = el && el.node
  if (!node || !node.isConnected || typeof node.focus !== 'function') return false
  if (typeof node.closest === 'function' && node.closest('[inert]')) return false
  const doc = node.ownerDocument
  const active = doc && doc.activeElement
  if (
    active &&
    active !== node &&
    active !== doc.body &&
    (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
  ) {
    return false
  }
  try {
    node.focus()
  } catch (_) {
    return false
  }
  return !doc || doc.activeElement === node
}

// "Use personal workspace" — the onboarding escape hatch. Persists the skip so
// the gate never re-opens, clears the draft, and closes the modal. The user
// stays in their personal org.
export function usePersonalWorkspace() {
  const root = _rootState(this)
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(ONBOARDING_SKIP_KEY, '1')
  } catch (err) {
    console.warn('[onboarding] failed to persist personal-workspace choice:', err)
  }
  root?.update({ ..._resetFields, _orgOnboardingSkipped: true })
  this.call('closeModal', CREATE_ORG_LAYER_ID)
}

// Does the new-account welcome own this person's FIRST workspace? Then the
// org gate (components/NoOrgOverlay.js) stays closed. ROUND 2 BLOCKER #5
// (elena-r2, sam-r2, 2026-10-26): a direct sign-up gets ONE personal
// workspace that holds the sign-up credits, and the welcome opens on it; the
// gate's "Create team's workspace" then opened by itself over the welcome (on
// a step, on a reload), and the person founded a SECOND organisation whose
// workspace was empty and had 0 credits. The welcome names and sets up the
// first workspace (Your work's name field — functions/onboardingPresets.js
// onboardingNameSave); a team is still one click away in the switcher.
//
// True when: this session decided the welcome, or the person's record holds
// any welcome decision (the welcome met them — a reload after it must not
// ask), or the account is new — inside the welcome's own 14-day window
// (functions/onboarding.js onboardingEligible, restated: FA204). An account
// with no readable creation date is not new.
export function orgOnboardingOwnedByWelcome(root) {
  const NEW_ACCOUNT_MS = 14 * 24 * 60 * 60 * 1000
  const r = root && typeof root === 'object' ? root : null
  if (!r) return false
  if (r._onboardingDecision) return true
  const prefs = r.userPrefs && typeof r.userPrefs === 'object' ? r.userPrefs : null
  if (prefs && prefs.onboarding && typeof prefs.onboarding === 'object') return true
  const user = r.user && typeof r.user === 'object' ? r.user : null
  const created = user && user.createdAt ? Date.parse(user.createdAt) : NaN
  if (!Number.isFinite(created)) return false
  return Date.now() - created <= NEW_ACCOUNT_MS
}
